/**
 * Plan adapter for stated dimension records (`room.dimensions[]`).
 *
 * The geometry is decided by the pure rules in `@shared-lib/dimensioning`; all
 * this module does is dress the result in the shape the floor-plan layout and
 * draw layers already consume, so a stated dimension takes exactly the same
 * path to the canvas as a generated one.
 *
 * These are INTERIOR dimensions: they run between two datums through a point
 * in the room, with no wall to hang off. `layoutMeasurements` places them
 * correctly without any new mechanism, because it groups by anchor line rather
 * than by wall — pinned in `__tests__/interiorDimensionLayout.test.js`.
 *
 * ONE CONSTRAINT worth knowing: a `{kind:'wall'}` datum resolves against the
 * wall segments, and those only carry ids in WALL mode. Polygon mode derives
 * its segments from the floor outline, which has no wall ids, so a stated
 * dimension naming a wall draws nothing there. Pinned in the tests.
 *
 * See claude-docs/2026-09-15-unified-dimensioning.md §3, §4.2.
 */

import {
  resolveDimensions,
  resolveDimensionMarks,
} from '@shared-lib/dimensioning';
import { metersToFeetInches } from './floorPlanUtils';
import { canonicalOpeningId } from './openingSyncUtils';

// Equal legs print the drafting convention rather than the same number twice.
const EQ_LABEL = 'EQ';

/**
 * The lookup the rules need: every placed thing in the room, by id.
 *
 * An opening is indexed under BOTH its ids. A room's copy of an opening has a
 * different id from the structure's canonical record, and the two spaces are
 * disjoint — a datum ref stores the CANONICAL id because that is the stable
 * one, while the items handed here are the room's copies. Indexing both is
 * what lets a stored ref find the geometry.
 */
export const buildDimensionContext = (allItems, wallSegments) => {
  const entitiesById = new Map();
  for (const item of allItems || []) {
    if (!item) continue;
    if (item.id) entitiesById.set(item.id, item);
    const canonical = canonicalOpeningId(item);
    if (canonical && !entitiesById.has(canonical)) {
      entitiesById.set(canonical, item);
    }
  }
  return { entitiesById, wallSegments: wallSegments || [] };
};

/**
 * The datum ref a clicked floor-plan item names, or null when the click
 * landed on nothing addressable. PURE.
 *
 * Openings key by their CANONICAL id (`structureOpeningId ?? id`): the room's
 * copy carries a different id, and a ref stored against that one stops
 * resolving the moment the room is re-hydrated.
 */
export const refForFloorplanItem = (item) => {
  if (!item?.id) return null;
  if (item.isWall) return { kind: 'wall', id: item.id };
  const isOpening =
    item.category?.door ||
    item.category?.window ||
    item.category?.opening ||
    item.isWindow ||
    item.isOpening;
  if (isOpening) {
    const id = canonicalOpeningId(item);
    return id ? { kind: 'opening', id } : null;
  }
  // A room label or the room itself is not something a dimension measures to.
  if (item.isRoomLabel || item.itemType === 'roomLabel') return null;
  return { kind: 'object', id: item.id };
};

const objectIdOf = (ref) =>
  ref && typeof ref === 'object' && (ref.kind === 'object' || !ref.kind)
    ? ref.id || null
    : null;

/**
 * One resolved leg, in the floor plan's measurement shape.
 *
 * The anchors ARE the measured points: an interior dimension's extension lines
 * start where it measures to, and `layoutMeasurements` offsets the drawn line
 * off that anchor line. `offsetStart` / `offsetEnd` seed it and are replaced.
 */
const toPlanMeasurement = (leg, index) => {
  const start = leg.from.point;
  const end = leg.to.point;
  return {
    type: 'fixture-node',
    subtype: 'dimension-record',
    tier: 'interior',
    rule: leg.rule,
    dimensionStyle: leg.style,
    start,
    end,
    distance: leg.distance,
    label: leg.style === 'eq' ? EQ_LABEL : metersToFeetInches(leg.distance),
    offsetStart: start,
    offsetEnd: end,
    extensionStartFrom: start,
    extensionStartTo: start,
    extensionEndFrom: end,
    extensionEndTo: end,
    // Where the dotted line carries BACK to, when the wall does not reach the
    // line this dimension was taken on. Separate from `extensionStartFrom`,
    // which the layout pass uses as the anchor it offsets the dim line off —
    // moving that would move the dimension itself.
    extensionStartAt: leg.from.anchor || null,
    extensionEndAt: leg.to.anchor || null,
    fromLabel: leg.rule,
    toLabel: leg.rule,
    fromObjectId: objectIdOf(leg.from.ref),
    toObjectId: objectIdOf(leg.to.ref),
    subjectId: objectIdOf(leg.subjectRef),
    relatedObjectIds: [],
    // Deliberately NOT lockable or value-editable: a stated dimension is moved
    // by restating it, not by dragging its number. `getMeasurementKey` returns
    // null without a lockKey, which keeps it out of the stretch lock store.
    editable: false,
    legIndex: index,
  };
};

/**
 * Every measurement the room's stated dimension records draw.
 * Returns [] when the room states none — which is every room until the
 * authoring UI lands.
 */
export const calculateRecordMeasurements = (room, allItems, wallSegments) => {
  const records = Array.isArray(room?.dimensions) ? room.dimensions : [];
  if (records.length === 0) return [];
  const ctx = buildDimensionContext(allItems, wallSegments);
  return [
    ...resolveDimensions(records, ctx).map(toPlanMeasurement),
    // Centrelines ride the same array so nothing new has to be threaded to
    // the canvas, but they carry their own type: the layout pass passes them
    // through untouched and the draw pass renders them as a drafting line
    // rather than a dimension.
    ...resolveDimensionMarks(records, ctx).map((mark) => ({
      ...mark,
      type: 'dimension-mark',
      subtype: 'centerline',
    })),
  ];
};

/**
 * The floor plan's highlight payload for a set of datum refs.
 *
 * `highlightedElements` carries ONE type at a time (`{ type, elements }`), so
 * a record whose datums are of mixed kinds highlights the members sharing the
 * FIRST datum's type. That is every realistic record — a centring names two
 * walls, or two cabinets — and the per-datum chips give exact certainty when
 * it is not.
 *
 * @param {Array} refs   datum refs.
 * @param {Object} room  the room, for resolving a wall ref to its wall.
 * @param {Map|Object} entitiesById  everything else, by id.
 * @returns {{type: string|null, elements: Array}}
 */
export const highlightForDatums = (refs, room, entitiesById) => {
  const EMPTY = { type: null, elements: [] };
  const list = (refs || []).filter(Boolean);
  if (list.length === 0) return EMPTY;

  const lookup = (id) =>
    entitiesById && typeof entitiesById.get === 'function'
      ? entitiesById.get(id)
      : entitiesById?.[id];

  const wallFor = (id) =>
    (room?.walls || []).find(
      (w) => w.id === id || w.alignment?.structureWallId === id
    ) || null;

  const resolved = list
    .map((ref) => {
      if (ref.kind === 'wall') {
        const wall = wallFor(ref.id);
        return wall ? { type: 'walls', element: wall } : null;
      }
      const entity = lookup(ref.id);
      if (!entity) return null;
      if (entity.category?.door) return { type: 'doors', element: entity };
      if (entity.category?.window) return { type: 'windows', element: entity };
      if (entity.category?.opening)
        return { type: 'openings', element: entity };
      return { type: 'objects', element: entity };
    })
    .filter(Boolean);

  if (resolved.length === 0) return EMPTY;
  const type = resolved[0].type;
  return {
    type,
    elements: resolved.filter((r) => r.type === type).map((r) => r.element),
  };
};

/**
 * Move a dimension's subject to `target`, in the room.
 *
 * A subject that belongs to a FIXTURE GROUP moves with it: a recessed row
 * shares one `fixtureGroupId` precisely so it travels as a unit, and pulling
 * one can out of its row to satisfy a dimension would break the thing the
 * grouping exists to hold together. The whole group translates by the same
 * delta, so its internal spacing survives.
 *
 * PURE. Returns the room unchanged when there is nothing to move.
 */
export const applyTargetToRoom = (room, subjectId, target) => {
  const objects = room?.objects || [];
  const subject = objects.find((o) => o.id === subjectId);
  if (!subject?.center || !target) return room;

  const dx = target.x - subject.center.x;
  const dz = target.z - subject.center.z;
  if (dx === 0 && dz === 0) return room;

  const groupId = subject.fixtureGroupId;
  const moves = (o) =>
    o.id === subjectId || (!!groupId && o.fixtureGroupId === groupId);
  const stamp = new Date().toISOString();

  return {
    ...room,
    objects: objects.map((o) =>
      moves(o) && o.center
        ? {
            ...o,
            center: { ...o.center, x: o.center.x + dx, z: o.center.z + dz },
            lastModified: stamp,
          }
        : o
    ),
  };
};

/** A measurement the user STATED, rather than one the generator produced. */
export const isStatedDimension = (m) =>
  m?.subtype === 'dimension-record' || m?.type === 'dimension-mark';

/**
 * Only the stated dimensions — for the layer that shows them on their own.
 *
 * The generated chains chase every fixture edge and opening on every wall, so
 * a stated dimension running through the middle of the room is lost among
 * them. PURE.
 */
export const onlyStatedDimensions = (measurements) =>
  (measurements || []).filter(isStatedDimension);
